/**
 * Runtime helpers over `Field` instances and the FIELD_TYPES registry. Registry
 * entries are flat, so helpers read `def.X` directly. This file is the one that
 * imports `types.ts`; a helper consumer imports it without the per-type files.
 */

import type { SchemaField } from "../registries/schemas.js"
import { capitalize, formatSnakeCase } from "../utils/stringFormatter.js"
import { hasActiveConditions, type SavedFilter, savedFilterProblem } from "./filter.js"
import type { AllGroups, MatchesGroup } from "./group.js"
import type { Compare } from "./sort.js"
import {
  DEFS,
  FIELD_TYPES,
  type FieldType,
  type FieldTypeName,
  type ReferenceFieldType,
} from "./types.js"

// PUBLIC TYPES ================================================================

/** What a collection reads off a field: identity, display, behaviour, accessors and
 *  grouping. The views and `useCollection` need nothing else. */
export interface Field {
  key: string
  type: FieldTypeName
  label?: string
  /** FontAwesome icon name. */
  icon: string
  /** Holds several values. */
  isArray?: boolean
  /** The options of a select field. */
  values?: readonly unknown[]
  /** A value as display text; carried from the field's type unless the field sets its own. */
  format?: (value: unknown) => string
  /** The hint an empty cell or input shows. */
  placeholder?: string
  /** The operator keys a filter offers for this field; the type's own when unset. */
  operators?: string[]
  isHidden?: boolean
  isFilterable?: boolean
  isEditable?: boolean
  /** Whether the field is editable on a given item; every item when unset. */
  isEditableFor?: (item: unknown) => boolean
  /** How two values of the field order, carried from its type. A field with no
   *  `sort` is not sortable; set it to `undefined` to opt a field out of its type's. */
  sort?: Compare
  /** The buckets a grouped collection shows, carried from the field's type. A field
   *  with both `allGroups` and `matchesGroup` is groupable; set either to `undefined`
   *  to opt a field out of its type's. */
  allGroups?: AllGroups
  /** Whether a value falls in a bucket, carried from the field's type. */
  matchesGroup?: MatchesGroup
  /** The display value of a row: resolved entities for a reference field, the
   *  stored value otherwise. */
  value: (item: unknown) => unknown
  /** The stored value a cell binds: target id(s) for a reference field, the same as
   *  `value` otherwise. */
  rawValue: (item: unknown) => unknown
}

/** A field instance: what consumers pass to the helpers below. It adds what
 *  `enrichField` resolves from a schema for core's own reads and writes.
 *  `FieldType` is its static type definition. */
export interface AugerField extends Field {
  /** The record type a link/record/type field scopes to. */
  recordType?: string
  /** The wire format of a reverse-link filter field (`reverse_links`). */
  subtype?: string
  /** The bare schema/data key when `key` is a namespaced column identity
   *  (`record.<k>` in the listing grid). Read `field.sourceKey ?? field.key`. */
  sourceKey?: string
  /** The id of the schema that owns the field. */
  schema_id?: number
  /** The field's key within its schema, stamped on its relation rows; `null` for
   *  the aggregate relations (`contacts`, `taggings`, …) and unset on fixed columns. */
  schema_key?: string | null
  /** Tells a relation field's rows from a sibling aggregate's when `schema_key`
   *  alone can't (`socials` within `contacts`). Relation writes use it in place of
   *  the `schema_key` comparison. */
  ownsRow?: (row: { schema_key?: string | null; [k: string]: unknown }) => boolean
  /** A lookup field's two-key path across a link. */
  path?: string
  /** A link field's filter on the records it can link to. */
  filter?: SavedFilter
  /** The key a time field logs spans of (see services/spans.ts). */
  tracks?: string
  description?: string
  /** Column width in pixels. */
  width?: number
  /** The source the field came from, for sectioning detail and listing views. */
  group?: "recordColumn" | "typeSchema" | "recordSchema" | "listingColumn" | "listingSchema"
  /** For a backlink or reverse-link field: the key of the link field it reads. */
  linkKey?: string
  /** For a backlink or reverse-link field: the record type that holds that link field. */
  sourceType?: string
}

/** A hand-authored field before enrichment; `key`, `type` and `label` are required. */
export type EnrichableField = Partial<AugerField> & { key: string; type: FieldTypeName; label: string }

export interface RecordTypeOption {
  value: string
  label: string
}

export interface SchemaFieldValidation {
  valid: boolean
  errors: string[]
}

// LOOKUP ======================================================================

/** The FieldType a name identifies; an unregistered name resolves to `string`. */
export function getFieldType(name: FieldTypeName | string): FieldType {
  if (FIELD_TYPES[name]) return FIELD_TYPES[name]
  return FIELD_TYPES.string!
}

/** A field's label by key; an undeclared key (a link key no schema named) reads as
 *  its title-cased self: "linked_portco" → "Linked Portco". */
export function getFieldLabel(fields: AugerField[], key: string, fallback?: string): string {
  return fields.find((f) => f.key === key)?.label || fallback || formatSnakeCase(key)
}

/** Whether `name` is a registered field type. Writes refuse an unknown name rather
 *  than route it through `getFieldType`'s `string` fallback. */
export function hasFieldType(name: string): name is FieldTypeName {
  return !!FIELD_TYPES[name]
}

/** Minimal field identity the type-discriminant helpers need: just the type.
 *  Both `Field` and looser shapes like `FilterFieldDef` satisfy it. */
type FieldTypeRef = { type: string }

/** Narrow a FieldType to a ReferenceFieldType by its `kind`. Callers with only a
 *  `{ type }` use the `as*`/`is*` helpers below. */
export function isReferenceDef(def: FieldType): def is ReferenceFieldType {
  return def.kind === "reference"
}

/** A link-junction def: a reference whose `relationship` is `"links"`. */
export function isLinkJunctionDef(def: FieldType): def is ReferenceFieldType {
  return isReferenceDef(def) && def.relationship === "links"
}

export function isValueField(field: FieldTypeRef): boolean {
  return getFieldType(field.type).kind === "value"
}

/** A junction: a reference whose inner `reference` (the hop to the real entity)
 *  is set — link/backlink/tagging/attachment. */
export function isJunctionField(field: FieldTypeRef): boolean {
  const def = getFieldType(field.type)
  return isReferenceDef(def) && def.reference != null
}

/** A plain reference (contact/time/source/user/schema/type/record/parent): a
 *  reference with no inner `reference`. */
export function isReferenceField(field: FieldTypeRef): boolean {
  const def = getFieldType(field.type)
  return isReferenceDef(def) && def.reference == null
}

/** The relations whose rows are a field's value, edited by adding and removing rows
 *  (`computeRelationChanges`): junctions (link/tagging/attachment) and value
 *  relations (contact/time/source). Other references hold a scalar id. */
export const RELATION_FIELDS = new Set([
  "links",
  "taggings",
  "attachments",
  "contacts",
  "times",
  "sources",
])
export function isRelationField(field: FieldTypeRef): boolean {
  const def = getFieldType(field.type)
  return isReferenceDef(def) && def.relationship != null && RELATION_FIELDS.has(def.relationship)
}

/** Whether a field is a select or any subtype of one (deal_status, task_status, …). */
export function isSelectField(field: FieldTypeRef): boolean {
  let def: FieldType | undefined = getFieldType(field.type)
  while (def) {
    if (def.name === "select") return true
    def = def.parent ? getFieldType(def.parent) : undefined
  }
  return false
}

/** A relation field's current set as ENTITIES, aligning `rawValue` (the id set)
 *  with `value` (the hydrated entities); an unresolvable target rides along as an
 *  `{ id }` placeholder so the set stays complete. The edit draft is seeded and
 *  committed as these entities. */
export function referenceEntities(
  field: Pick<Field, "value" | "rawValue">,
  item: unknown
): Array<{ id: number | string; [k: string]: unknown }> {
  const toArr = (v: unknown) => (v == null ? [] : Array.isArray(v) ? v : [v])
  const resolved = toArr(field.value(item)).filter(
    (e): e is { id: number | string } => !!e && typeof e === "object" && "id" in e
  )
  const byId = new Map(resolved.map((e) => [String(e.id), e]))
  return toArr(field.rawValue(item)).map(
    (id) => byId.get(String(id)) ?? { id: id as number | string }
  )
}

// RECORD-TYPE INTROSPECTION ===================================================
// Only `link` / `record` / `type` fields are scoped to a record type; every other
// distinction is encoded in the `type` itself.

export function getRecordTypeOptions(typeName: string): RecordTypeOption[] {
  const def = getFieldType(typeName)
  if (!isReferenceDef(def) || !def.recordTypes) return []
  return def.recordTypes().map((value) => ({ value, label: capitalize(value) }))
}

export function typeHasRecordTypes(typeName: string): boolean {
  const def = getFieldType(typeName)
  return isReferenceDef(def) && def.recordTypes !== undefined
}

/** Whether a field type can hold multiple values (the schema editor's
 *  "Allow multiple" toggle). References and `select` support arrays; plain
 *  scalars do not. */
export function typeSupportsArray(typeName: string): boolean {
  return getFieldType(typeName).kind === "reference" || typeName === "select"
}

// FORMAT / PARSE ==============================================================
// `format`/`parse` are FieldType behaviors. Prefer the one baked onto the field
// by enrichment; for a bare field (just a `type`), fall back to the type's own;
// if neither defines one, the value passes through unchanged.

export function formatValue(field: FieldTypeRef & { format?: unknown }, value: unknown): unknown {
  const fn = field.format ?? getFieldType(field.type).format
  return typeof fn === "function" ? fn(value) : value
}

export function parseValue(field: FieldTypeRef & { parse?: unknown }, input: unknown): unknown {
  const fn = field.parse ?? getFieldType(field.type).parse
  return typeof fn === "function" ? fn(input) : input
}

// DECLARABLE TYPES ============================================================

/** The field types a user may declare on a schema: every concrete registry entry
 *  (one with a `parent`). The storage roots (value / reference / computed) are
 *  abstract kinds and never offered; the schema editor's type picker uses the same
 *  rule. */
export function declarableFieldTypes(): string[] {
  return DEFS.filter((t) => t.parent !== undefined).map((t) => t.name)
}

// CLEANING ====================================================================

/** A field as authored by an editor or an API caller, before it is cleaned for
 *  storage. `values` may be an array or a comma-separated string. */
export interface SchemaFieldDraft {
  key?: string | null
  type?: string | null
  label?: string | null
  description?: string | null
  isHidden?: boolean | null
  isArray?: boolean | null
  recordType?: string | null
  values?: unknown
  path?: string | null
  filter?: unknown
}

/** The properties cleanSchemaFields shapes itself. Any other property is kept
 *  as given: a field re-saved by a schema edit must not lose data the editor
 *  does not know about. */
const MANAGED_FIELD_PROPS = new Set([
  "key",
  "type",
  "label",
  "description",
  "isHidden",
  "isArray",
  "recordType",
  "values",
  "path",
  "filter",
])

/** Row-tracking properties the schema editor adds; never stored. */
const EDITOR_ONLY_PROPS = new Set(["tempKey", "isNew"])

/** Shape field definitions for storage on `schemas.fields`: drop fields lacking a
 *  key or label, default `type` to "string", and keep each optional property only
 *  when set. Select-family fields keep `values` (a comma-separated string is
 *  split); `lookup` fields keep `path`; `link` fields keep a `filter` that has a
 *  condition to apply. Editor-only properties are dropped. A property this does
 *  not manage is kept as given. */
export function cleanSchemaFields(fields: readonly SchemaFieldDraft[]): SchemaField[] {
  if (!Array.isArray(fields)) return []

  return fields
    .filter((f) => f.key && f.label)
    .map((field) => {
      const type = field.type || "string"
      if (!hasFieldType(type)) {
        throw new Error(`cleanSchemaFields: field "${field.key}" has unknown type "${type}"`)
      }
      // Properties this does not manage ride along at run time, so a re-save
      // keeps them; the SchemaField type names only the properties code reads.
      const unmanaged: Record<string, unknown> = {}
      for (const [prop, value] of Object.entries(field)) {
        if (MANAGED_FIELD_PROPS.has(prop) || EDITOR_ONLY_PROPS.has(prop)) continue
        if (value !== undefined) unmanaged[prop] = value
      }
      const cleaned: SchemaField = {
        ...unmanaged,
        key: field.key as string,
        type,
        label: field.label as string,
      }

      if (field.description) cleaned.description = field.description
      if (field.isHidden) cleaned.isHidden = true
      if (field.isArray) cleaned.isArray = true

      if (field.recordType) cleaned.recordType = field.recordType

      if (isSelectField({ type }) && field.values) {
        if (typeof field.values === "string") {
          cleaned.values = field.values
            .split(",")
            .map((v: string) => v.trim())
            .filter(Boolean)
        } else if (Array.isArray(field.values)) {
          cleaned.values = field.values
        }
      }

      if (type === "lookup" && field.path) cleaned.path = field.path

      if (type === "link" && hasActiveConditions(field.filter as SavedFilter | null)) {
        cleaned.filter = field.filter as SavedFilter
      }

      return cleaned
    })
}

// VALIDATION ==================================================================

/** A lookup `path` split into its two keys: the link field on this record whose
 *  rows it follows, and the field it reads on each linked record. Null unless
 *  the path is exactly two snake_case keys joined by a dot. */
export function parseLookupPath(path: string): { via: string; pick: string } | null {
  const match = /^([a-z_][a-z0-9_]*)\.([a-z_][a-z0-9_]*)$/.exec(path)
  return match ? { via: match[1]!, pick: match[2]! } : null
}

export function validateSchemaField(
  field: Partial<AugerField> | SchemaFieldDraft
): SchemaFieldValidation {
  const errors: string[] = []
  const key = (typeof field.key === "string" ? field.key : "").trim()
  const type = (typeof field.type === "string" ? field.type : "").trim()
  const label = (typeof field.label === "string" ? field.label : "").trim()

  if (!key) errors.push("Field key is required")
  else if (key.length < 2) errors.push("Field key must be at least 2 characters")

  if (!type) errors.push("Field type is required")
  if (!label) errors.push("Field label is required")
  else if (label.length < 2) errors.push("Field label must be at least 2 characters")

  if (key && !/^[a-z_][a-z0-9_]*$/.test(key)) {
    errors.push("Field key must be lowercase with underscores (e.g., field_name)")
  }

  const def = type ? FIELD_TYPES[type] : null
  if (type && !def) {
    errors.push(`Unknown field type: ${type}`)
  } else if (def) {
    const requiredProps = def.requiredProps
    if (requiredProps) {
      // A required prop is satisfied by the field OR by the type def itself:
      // subtypes like deal_status/task_status define `values` on the type, so
      // the schema editor shouldn't demand the user re-enter them (unlike bare
      // `select`, whose def carries no values).
      const typeDefaults: Record<string, unknown> = Object.fromEntries(Object.entries(def))
      const given: Record<string, unknown> = Object.fromEntries(Object.entries(field))
      for (const prop of requiredProps) {
        if (!given[prop] && !typeDefaults[prop]) {
          errors.push(`${prop} is required for ${field.type} fields`)
        }
      }
    }
    if (field.recordType) {
      const recordTypesFn = isReferenceDef(def) ? def.recordTypes : undefined
      const recordTypes = typeof recordTypesFn === "function" ? recordTypesFn() : []
      if (!recordTypes.includes(field.recordType)) {
        errors.push(
          `Invalid record type "${field.recordType}" for ${field.type}. Valid: ${recordTypes.join(", ") || "(none)"}`
        )
      }
    }
  }

  if (type === "select" && (!Array.isArray(field.values) || field.values.length === 0)) {
    errors.push("Select fields require a non-empty values array")
  }

  if (field.filter != null) {
    const problem = savedFilterProblem(field.filter)
    if (problem) errors.push(`Filter ${problem}`)
    else if (hasActiveConditions(field.filter as SavedFilter)) {
      if (type !== "link") errors.push("Only link fields take a filter")
      else if (!field.recordType) errors.push("A filtered link field needs a record type to filter")
    }
  }

  if (type === "lookup" && typeof field.path === "string" && !parseLookupPath(field.path)) {
    errors.push(
      "Lookup path must be a link field and a field on the linked record (e.g., organization.founded)"
    )
  }

  return { valid: errors.length === 0, errors }
}
